import Container from '../atoms/container/mainContainer';
import Focus from '../../../shared/types/enums/focus';
import HorizontalAnimation from '../atoms/container/horizontalAnimationContainer';
import MainContext from '../../../shared/components/utils/context/mainContext';
import Props from '../../../shared/types/props/main';
import Rails from '../sections/main/rails';
import ScreenUtils from '../../../shared/utils/screen';
import Sliders from '../sections/main/sliders';
import VerticalAnimation from '../atoms/container/verticalAnimationContainer';
import TEmpty from './empty';
import useBackKeyboard from '../../../shared/components/utils/hooks/useBackKeyboard';
import useKeyboard from '../../../shared/components/utils/hooks/useKeyboard';
import useMain from '../../../shared/components/utils/hooks/useMain';
import { popRoute } from '../../../shared/store/thunks/route.thunk';
import { setFocus } from '../../../shared/store/slices/focus.slice';
import { store } from '../../../shared/store/store';
import { useAppDispatch } from '../../../shared/store/hooks';

const Y_AXIS_OFFSET = 440;

function TMain(props: Props) {
  const dispatch = useAppDispatch();

  // Main hook

  const data = useMain(props);
  const {
    completeRef,
    currentRail,
    display,
    hasFilters,
    lastRail,
    lastSlider,
    prevFocus,
    railsRef,
    slidersRef,
    horizontalAnimation,
    verticalAnimation,
  } = data;

  // Keyboard hooks

  useBackKeyboard({
    callback: () => {
      const { focus: focusSlice, router } = store.getState();
      if (focusSlice.focus === Focus.MENU || router.route.stack.length) {
        dispatch(popRoute());
      } else {
        prevFocus.current = focusSlice.focus as Focus | Focus[];
        dispatch(setFocus(Focus.MENU));
      }
    },
  });

  useKeyboard({
    canBeFocused: false,
    keyDownHandler: (key) => {
      const { focus: focusSlice } = store.getState();
      // If no content, return
      if (!railsRef.current.length && !slidersRef.current.length) {
        return;
      }
      // Else, focus sliders or rails
      if (
        completeRef.current &&
        focusSlice.focus === Focus.MENU &&
        (key === 'RIGHT' || key === 'SELECT')
      ) {
        const fallbackFocus =
          (focusSlice?.stack?.length ? focusSlice.stack[0] : null) ||
          (!slidersRef.current.length
            ? [Focus.RAILS_LIST, parseInt(Focus.RAILS_SUB_LIST + '0')]
            : Focus.SLIDERS);
        setTimeout(() => {
          dispatch(
            setFocus(prevFocus.current ? prevFocus.current : fallbackFocus)
          );
          prevFocus.current = undefined;
        }, 150);
      }
    },
  });

  // Components data

  const animationProps = {
    horizontal: {
      animation:
        !completeRef.current && props.startsInMenu
          ? 'main-content-load'
          : horizontalAnimation
          ? 'main-content-out'
          : 'main-content-in',
      force: true,
    },
    vertical: {
      animation: 'main-content-vertical',
      style: {
        transform: verticalAnimation
          ? 'translateY(0px)'
          : `translateY(-${Y_AXIS_OFFSET * ScreenUtils.widthRatio}px)`,
      },
    },
  };

  const contextValue = {
    ...data,
    hasFilters,
    animation: { vertical: verticalAnimation },
    complete: completeRef.current,
    lastRail: lastRail.current,
    lastSlider: lastSlider.current,
    index: currentRail,
    prevFocus: prevFocus.current,
    rails: railsRef.current,
    sliders: slidersRef.current,
  };

  return display?.empty ? (
    <TEmpty message={props.message} />
  ) : (
    <>
      <MainContext.Provider value={contextValue}>
        <Container>
          <HorizontalAnimation {...animationProps.horizontal}>
            <VerticalAnimation {...animationProps.vertical}>
              <Sliders />
              <Rails />
            </VerticalAnimation>
          </HorizontalAnimation>
        </Container>
      </MainContext.Provider>
    </>
  );
}

export default TMain;
